import React, { useRef, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import '../styles/AddDocument.css';
import { FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { toast } from 'react-toastify';
import { DOCUMENT_TYPE_OPTGROUPS } from '../constants/documentTypes';
import {
  fetchAllCompanyUsers,
  isWorkforcePerson,
  workforceRoleLabel,
} from '../utils/workforcePeople';

const GET_ALL_USERS = `
  query GetAllUser($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        employeeCode
        roles
        role
      }
      totalPages
    }
  }
`;

const GENERATE_UPLOAD_URL = `
  mutation WorkforceUpload($userId: String!, $fileName: String!, $contentType: String!) {
    createWorkforceDocumentUploadUrl(userId: $userId, fileName: $fileName, contentType: $contentType) { key url }
  }
`;

const CREATE_WORKFORCE_DOCUMENT = `
  mutation CreateWorkforceDocument($input: CreateWorkforceDocumentInput!) {
    createWorkforceDocument(input: $input) {
      id
    }
  }
`;

export default function AddDocument({
  onClose,
  onDocumentAdded,
  initialUserId = '',
  initialEmployeeCode = '',
}) {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: '',
    documentType: '',
    cleaningLicense: '',
    experience: '',
    expiryDate: '',
    siteReference: '',
  });
  const [otherDocumentType, setOtherDocumentType] = useState('');
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const employeeBoxRef = useRef(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const allUsers = await fetchAllCompanyUsers(graphqlRequest, GET_ALL_USERS);

        const workforceUsers = allUsers.filter(isWorkforcePerson);

        const prefixCount = { ADM: 0, MGM: 0, SUP: 0, CLR: 0, CTR: 0, EMP: 0 };
        const allEmployees = workforceUsers
          .map((user) => {
            const name =
              `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
            const role = workforceRoleLabel(user.roles, user.role);
            let prefix = 'EMP';
            if (role === 'Admin') prefix = 'ADM';
            else if (role === 'Manager') prefix = 'MGM';
            else if (role === 'Supervisor') prefix = 'SUP';
            else if (role === 'Cleaner') prefix = 'CLR';
            else if (role === 'Contractor') prefix = 'CTR';

            const realCode = String(user.employeeCode || '').trim().toUpperCase();
            let employeeId = realCode;
            if (!employeeId) {
              prefixCount[prefix] += 1;
              employeeId = `${prefix}${String(prefixCount[prefix]).padStart(3, '0')}`;
            }

            return {
              employeeId,
              dbId: user.id,
              name,
              role,
              label: `${employeeId} — ${name} (${role})`,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setEmployees(allEmployees);

        const preselect =
          allEmployees.find((e) => e.dbId === initialUserId) ||
          allEmployees.find(
            (e) =>
              e.employeeId === String(initialEmployeeCode || '').trim().toUpperCase(),
          );
        if (preselect) {
          setFormData((prev) => ({ ...prev, employeeId: preselect.employeeId }));
          setEmployeeQuery(preselect.label);
        }
      } catch (err) {
        console.error('Error fetching employees:', err);
        toast.error(err?.message || 'Failed to fetch employees');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [initialUserId, initialEmployeeCode]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!employeeBoxRef.current?.contains(e.target)) {
        setEmployeeOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectedEmployee = employees.find(
    (emp) => emp.employeeId === formData.employeeId,
  );

  const filteredEmployees = useMemo(() => {
    const q = employeeQuery.trim().toLowerCase();
    if (!q || (selectedEmployee && employeeQuery === selectedEmployee.label)) {
      return employees;
    }
    return employees.filter(
      (emp) =>
        emp.employeeId.toLowerCase().includes(q) ||
        emp.name.toLowerCase().includes(q) ||
        emp.role.toLowerCase().includes(q) ||
        emp.label.toLowerCase().includes(q),
    );
  }, [employees, employeeQuery, selectedEmployee]);

  const pickEmployee = (emp) => {
    setFormData((prev) => ({ ...prev, employeeId: emp.employeeId }));
    setEmployeeQuery(emp.label);
    setEmployeeOpen(false);
  };

  const handleEmployeeQueryChange = (e) => {
    const value = e.target.value;
    setEmployeeQuery(value);
    setEmployeeOpen(true);
    if (selectedEmployee && value !== selectedEmployee.label) {
      setFormData((prev) => ({ ...prev, employeeId: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'documentType' && value !== 'Other') setOtherDocumentType('');
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const getFileUrl = (file) => URL.createObjectURL(file);

  const handleDragOver = (e) => e.preventDefault();
  const handleBoxClick = () => fileInputRef.current?.click();
  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEmployee) return toast.error('Please select an employee');
    if (!formData.documentType) return toast.error('Please select document type');
    if (formData.documentType === 'Other' && !otherDocumentType.trim())
      return toast.error('Please enter document type');
    if (!formData.cleaningLicense) return toast.error('Please select Cleaning License status');
    if (!formData.experience) return toast.error('Please enter years of experience');
    if (files.length === 0) return toast.error('Please upload at least one file');

    setUploading(true);
    try {

      const documentType =
        formData.documentType === 'Other' ? otherDocumentType.trim() : formData.documentType;

      await Promise.all(
        files.map(async (file, idx) => {
          const uploadData = await graphqlRequest(GENERATE_UPLOAD_URL, {
            userId: selectedEmployee.dbId,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
          });
          const target = uploadData?.createWorkforceDocumentUploadUrl;
          if (!target?.url || !target?.key) throw new Error('Failed to prepare document upload');
          let finalFileKey = target.key;

          try {
            await axios.put(target.url, file, {
              headers: { 'Content-Type': file.type || 'application/octet-stream' },
            });
          } catch (uploadErr) {
            // Fallback to inline data URL when direct S3 upload is blocked (e.g. CORS/policy).
            finalFileKey = await readFileAsDataUrl(file);
          }

          await graphqlRequest(CREATE_WORKFORCE_DOCUMENT, {
            input: {
              userId: selectedEmployee.dbId,
              employeeCode: selectedEmployee.employeeId,
              employeeName: selectedEmployee.name,
              documentType,
              cleaningLicense: formData.cleaningLicense,
              experienceYears: Number(formData.experience),
              fileName: file.name,
              fileType: file.type || '',
              fileSize: Number(file.size || 0),
              fileKey: finalFileKey,
              expiryDate: formData.expiryDate || null,
              siteReference: formData.siteReference.trim() || null,
            },
          });
        }),
      );

      if (onDocumentAdded) onDocumentAdded();
      toast.success(`${files.length} file(s) uploaded successfully!`);
      window.setTimeout(() => {
        window.dispatchEvent(new Event('admin-backend-notifications-refresh'));
        window.dispatchEvent(new Event('workforce-documents-refresh'));
      }, 400);
      setFormData({
        employeeId: '',
        documentType: '',
        cleaningLicense: '',
        experience: '',
        expiryDate: '',
        siteReference: '',
      });
      setOtherDocumentType('');
      setEmployeeQuery('');
      setEmployeeOpen(false);
      setFiles([]);
      onClose();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error?.message || 'Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="add-document-workforce-overlay">
      <div className="add-document-workforce-container">
        <div className="add-document-workforce-navbar">
          <h3 className="add-document-workforce-title">Add Document</h3>
          <button className="add-document-workforce-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form className="add-document-workforce-form" onSubmit={handleSubmit}>
          <label className="add-document-workforce-label">
            Employee <span className="required-star">*</span>
          </label>
          <div className="add-document-employee-combo" ref={employeeBoxRef}>
            <input
              type="text"
              value={employeeQuery}
              onChange={handleEmployeeQueryChange}
              onFocus={() => setEmployeeOpen(true)}
              placeholder={
                loading
                  ? 'Loading employees...'
                  : 'Type name or ID to choose employee'
              }
              className="add-document-workforce-input"
              disabled={loading}
              autoComplete="off"
              required={!selectedEmployee}
            />
            {employeeOpen && !loading && (
              <ul
                className="add-document-employee-list"
                role="listbox"
                onWheel={(e) => e.stopPropagation()}
              >
                {filteredEmployees.length === 0 ? (
                  <li className="add-document-employee-empty">No employees found</li>
                ) : (
                  filteredEmployees.map((emp) => (
                    <li key={emp.dbId}>
                      <button
                        type="button"
                        className={`add-document-employee-option${
                          formData.employeeId === emp.employeeId ? ' is-selected' : ''
                        }`}
                        onClick={() => pickEmployee(emp)}
                      >
                        <span className="add-document-employee-code">
                          {emp.employeeId}
                        </span>
                        <span className="add-document-employee-name">
                          {emp.name}
                        </span>
                        <span className="add-document-employee-role">{emp.role}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
          {selectedEmployee && (
            <div className="employee-name-display">
              <strong>Selected:</strong> {selectedEmployee.employeeId} —{' '}
              {selectedEmployee.name}
            </div>
          )}

          <label className="add-document-workforce-label">
            Document Type <span className="required-star">*</span>
          </label>
          <select
            name="documentType"
            value={formData.documentType}
            onChange={handleChange}
            className="add-document-workforce-select"
            required
          >
            <option value="">Select Document</option>
            {DOCUMENT_TYPE_OPTGROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {formData.documentType === 'Other' && (
            <input
              type="text"
              value={otherDocumentType}
              onChange={(e) => setOtherDocumentType(e.target.value)}
              placeholder="Enter document type"
              className="add-document-workforce-input"
              style={{ marginTop: '8px' }}
              required
            />
          )}

          <label className="add-document-workforce-label">
            Cleaning License <span className="required-star">*</span>
          </label>
          <select
            name="cleaningLicense"
            value={formData.cleaningLicense}
            onChange={handleChange}
            className="add-document-workforce-select"
            required
          >
            <option value="">Select</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>

          <label className="add-document-workforce-label">
            Experience (Years) <span className="required-star">*</span>
          </label>
          <input
            type="number"
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            min="0"
            max="50"
            placeholder="Enter years of experience"
            className="add-document-workforce-input"
            required
          />

          <label className="add-document-workforce-label">Expiry Date</label>
          <input
            type="date"
            name="expiryDate"
            value={formData.expiryDate}
            onChange={handleChange}
            className="add-document-workforce-input"
          />

          <label className="add-document-workforce-label">
            Site / Project Reference
          </label>
          <input
            type="text"
            name="siteReference"
            value={formData.siteReference}
            onChange={handleChange}
            placeholder="Optional; use for site-specific inductions"
            className="add-document-workforce-input"
            maxLength="255"
          />

          <label className="add-document-workforce-label">Upload</label>
          <div
            className="add-document-workforce-upload-box"
            onClick={handleBoxClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*,.pdf,.doc,.docx"
              multiple
              style={{ display: 'none' }}
            />
            <div className="add-document-workforce-upload-content">
              <FaCloudUploadAlt className="add-document-workforce-upload-icon" />
              <p>
                <span className="add-document-workforce-browse">Click to browse</span> or drag and drop
              </p>
              {files.length > 0 && (
                <p style={{ fontSize: '12px', marginTop: '8px', color: '#28a745' }}>
                  {files.length} file(s) selected
                </p>
              )}
            </div>
          </div>

          {files.length > 0 && (
            <div className="uploaded-files-list">
              <label className="add-document-workforce-label" style={{ marginBottom: '8px' }}>
                Personal Documents:
              </label>
              <div className="files-container">
                {files.map((file, index) => (
                  <div key={index} className="file-item">
                    <a
                      href={getFileUrl(file)}
                      download={file.name}
                      className="file-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.name}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="remove-file-btn"
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="add-document-workforce-btn-row">
            <button type="submit" className="add-document-workforce-upload-btn" disabled={uploading || loading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

